import React, { useState, useRef, useCallback } from 'react';
import {
  UploadCloud,
  FileText,
  CheckCircle,
  AlertCircle,
  Download,
  Weight,
} from 'lucide-react';
import { FileUploadResponse, TaskStartResponse, TaskStatus, FormState } from './types';
import './styles/tailwind.css';

const POLL_INTERVAL = 1500;

const Spinner: React.FC = () => (
  <svg
    className="animate-spin h-5 w-5 text-white"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const App: React.FC = () => {
  const [state, setState] = useState<FormState>({
    file: null,
    isUploading: false,
    isProcessing: false,
    activeAction: null,
    progress: 0,
    total: 0,
    result: null,
    weightResult: null,
    error: null,
  });
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileIdRef = useRef<string | null>(null);

  const {
    file,
    isUploading,
    isProcessing,
    activeAction,
    progress,
    total,
    result,
    weightResult,
    error,
  } = state;

  const updateState = (patch: Partial<FormState>) => {
    setState((prev) => ({ ...prev, ...patch }));
  };

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (taskId: string, action: 'stickers' | 'weight') => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const res = await fetch(`/api/task/${taskId}`);
          if (!res.ok) return;
          const data: TaskStatus = await res.json();

          if (data.status === 'completed') {
            stopPolling();
            const patch: Partial<FormState> = {
              isProcessing: false,
              activeAction: null,
              progress: data.total ?? 0,
              total: data.total ?? 0,
            };
            if (action === 'stickers') {
              patch.result = data;
            } else {
              patch.weightResult = data;
            }
            updateState(patch);
          } else if (data.status === 'error') {
            stopPolling();
            updateState({
              isProcessing: false,
              activeAction: null,
              error: data.error || 'Ошибка при обработке',
            });
          } else {
            updateState({
              progress: data.progress ?? 0,
              total: data.total ?? 0,
            });
          }
        } catch {
          /* keep polling on network hiccup */
        }
      }, POLL_INTERVAL);
    },
    [stopPolling],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    fileIdRef.current = null;
    updateState({
      file: e.target.files?.[0] || null,
      error: null,
      result: null,
      weightResult: null,
    });
  };

  const uploadFile = async (): Promise<string> => {
    if (fileIdRef.current) return fileIdRef.current;

    const formData = new FormData();
    formData.append('file', file!);

    const uploadRes = await fetch('/api/upload', { method: 'POST', body: formData });
    if (!uploadRes.ok) {
      const err = await uploadRes.json();
      throw new Error(err.detail || 'Ошибка при загрузке файла');
    }
    const uploadData: FileUploadResponse = await uploadRes.json();
    fileIdRef.current = uploadData.file_id;
    return uploadData.file_id;
  };

  const runAction = async (action: 'stickers' | 'weight') => {
    if (!file) {
      updateState({ error: 'Пожалуйста, выберите файл Excel' });
      return;
    }
    if (!file.name.endsWith('.xlsx')) {
      updateState({ error: 'Пожалуйста, выберите файл формата Excel (.xlsx)' });
      return;
    }

    updateState({
      isUploading: true,
      error: null,
      progress: 0,
      total: 0,
      activeAction: action,
      ...(action === 'stickers' ? { result: null } : { weightResult: null }),
    });

    try {
      const fileId = await uploadFile();
      updateState({ isUploading: false, isProcessing: true });

      const endpoint =
        action === 'stickers' ? `/api/process/${fileId}` : `/api/weight/${fileId}`;

      const processRes = await fetch(endpoint, { method: 'POST' });
      if (!processRes.ok) {
        const err = await processRes.json();
        throw new Error(err.detail || 'Ошибка при запуске обработки');
      }
      const { task_id }: TaskStartResponse = await processRes.json();

      startPolling(task_id, action);
    } catch (err) {
      stopPolling();
      updateState({
        isUploading: false,
        isProcessing: false,
        activeAction: null,
        error: err instanceof Error ? err.message : 'Произошла неизвестная ошибка',
      });
    }
  };

  const handleDownload = async () => {
    if (!result?.result_file_id) return;
    try {
      const res = await fetch(`/api/download/${result.result_file_id}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Ошибка при скачивании результата');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `result_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      updateState({
        error: err instanceof Error ? err.message : 'Произошла неизвестная ошибка',
      });
    }
  };

  const busy = isUploading || isProcessing;
  const progressPct = total > 0 ? Math.round((progress / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-indigo-900 mb-2">
            GGRN&WB Helper
          </h1>
          <p className="text-lg text-gray-600">
            Загрузите Excel файл с штрихкодами для получения стикеров или расчёта веса
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-xl p-8">
          <div className="space-y-6">
            {/* File picker */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Excel файл со штрихкодами
              </label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center ${
                  file
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-300 hover:border-indigo-400'
                } transition-colors duration-200`}
              >
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {file ? (
                    <div className="flex items-center justify-center space-x-2">
                      <FileText className="text-green-600" size={24} />
                      <span className="text-green-700 font-medium">{file.name}</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-center">
                        <UploadCloud className="text-indigo-500" size={40} />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">
                          <span className="text-indigo-600 font-medium">
                            Нажмите для выбора
                          </span>{' '}
                          или перетащите файл сюда
                        </p>
                        <p className="text-xs text-gray-500">Excel формат (.xlsx)</p>
                      </div>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <div className="flex items-center">
                  <AlertCircle className="text-red-500 mr-2 flex-shrink-0" size={20} />
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            )}

            {/* Progress bar */}
            {isProcessing && total > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>
                    {activeAction === 'weight'
                      ? 'Расчёт веса...'
                      : 'Обработка штрихкодов...'}
                  </span>
                  <span>
                    {progress} / {total} ({progressPct}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-indigo-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex justify-center gap-4">
              <button
                onClick={() => runAction('stickers')}
                disabled={busy}
                className={`px-6 py-3 rounded-lg font-medium text-white ${
                  busy ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
                } transition-colors duration-200 flex items-center space-x-2`}
              >
                {busy && activeAction === 'stickers' ? (
                  <>
                    <Spinner />
                    <span>{isUploading ? 'Загрузка...' : 'Обработка...'}</span>
                  </>
                ) : (
                  <span>Получить стикеры</span>
                )}
              </button>

              <button
                onClick={() => runAction('weight')}
                disabled={busy}
                className={`px-6 py-3 rounded-lg font-medium text-white ${
                  busy ? 'bg-amber-400' : 'bg-amber-600 hover:bg-amber-700'
                } transition-colors duration-200 flex items-center space-x-2`}
              >
                {busy && activeAction === 'weight' ? (
                  <>
                    <Spinner />
                    <span>{isUploading ? 'Загрузка...' : 'Расчёт...'}</span>
                  </>
                ) : (
                  <>
                    <Weight size={20} />
                    <span>Рассчитать вес</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Sticker result */}
          {result && result.status === 'completed' && (
            <div className="mt-8 p-6 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="text-green-600" size={24} />
                  <h3 className="text-lg font-medium text-green-800">
                    Стикеры готовы
                  </h3>
                </div>
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center space-x-2 transition-colors duration-200"
                >
                  <Download size={18} />
                  <span>Скачать результат</span>
                </button>
              </div>
              <div className="bg-white rounded-md p-4 border border-green-100">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Обработано товаров:</p>
                    <p className="text-xl font-semibold text-gray-800">
                      {result.total_items}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Найдено штрихкодов:</p>
                    <p className="text-xl font-semibold text-gray-800">
                      {result.found_barcodes}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Время:</p>
                    <p className="text-xl font-semibold text-gray-800">
                      {result.processing_time?.toFixed(1)}с
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Weight result */}
          {weightResult && weightResult.status === 'completed' && (
            <div className="mt-8 p-6 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center space-x-2 mb-4">
                <Weight className="text-amber-600" size={24} />
                <h3 className="text-lg font-medium text-amber-800">
                  Вес поставки рассчитан
                </h3>
              </div>
              <div className="bg-white rounded-md p-4 border border-amber-100">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Общий вес:</p>
                    <p className="text-2xl font-bold text-amber-700">
                      {weightResult.total_weight} кг
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Строк в файле:</p>
                    <p className="text-xl font-semibold text-gray-800">
                      {weightResult.total_items}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Время:</p>
                    <p className="text-xl font-semibold text-gray-800">
                      {weightResult.processing_time?.toFixed(1)}с
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 text-center text-gray-500 text-sm">
          <p>&copy; 2025 GGRN&WB Helper</p>
        </div>
      </div>
    </div>
  );
};

export default App;
